import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Device } from "@twilio/voice-sdk";
import { startIncomingCallRingtone, stopIncomingCallRingtone } from "../notifications/soundUtils";
import {
  startIncomingCallAttention,
  stopIncomingCallAttention,
} from "../utils/incomingCallAttention";
import {
  detectDeviceClass,
  shouldReportSoftphoneReady,
} from "../utils/voiceSoftphonePresence";
import { getTwilioVoiceToken, originatePabxCall, recordVoiceCallLog, updateTwilioVoicePresence, createTwilioClickToCall, hangupTwilioCall, joinVoiceLiveCall as joinVoiceLiveCallApi } from "../api/client";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { normalizeDialPhone } from "../utils/phone";
import {
  humanizeVoiceCallError,
  isTwilioAccessTokenError,
  isTwilioTransientConnectionError,
  shouldSurfaceTwilioDeviceError,
  twilioErrorCode,
  twilioErrorRawMessage,
} from "../utils/voiceCallErrors";
import { openCallPopupWindow } from "../utils/openCallPopupWindow";
import {
  publishActiveCallSession,
  publishCallCleared,
  publishCallAction,
  publishIncomingRing,
  publishIncomingCleared,
  serializeOutgoingCall,
  subscribeCallSessionBridge,
} from "../utils/callSessionBridge";
import { getApiBaseURL } from "../api/apiBaseUrl";
import { readTabBearerToken } from "../utils/tabAuthStorage";

const CallContext = createContext();

function resolveLeadIdFromIncoming(call, pending) {
  const params = call?.parameters || {};
  let fromCustom = null;
  try {
    const cp = call?.customParameters;
    if (cp && typeof cp.get === "function") {
      fromCustom = cp.get("LeadId") || cp.get("leadId");
    } else if (cp && typeof cp === "object") {
      fromCustom = cp.LeadId || cp.leadId;
    }
  } catch (_) {
    /* ignore */
  }
  const fromParams = Number(params.LeadId || params.leadId || fromCustom || 0) || null;
  if (fromParams) return fromParams;
  return Number(pending?.leadId || 0) || null;
}

async function logVoiceAttempt(channelId, payload) {
  if (!channelId || !payload?.leadId) return;
  try {
    await recordVoiceCallLog(channelId, payload);
  } catch (err) {
    console.warn("Falha ao registrar tentativa de ligação na timeline:", err);
  }
}

function resolveAttendedModeFromCall(params = {}, callState = null) {
  const raw =
    params?.AttendedMode ||
    params?.attendedMode ||
    callState?.attendedMode ||
    null;
  const m = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (!m) {
    if (callState?.isLiveJoin) return "live_join";
    return null;
  }
  if (m === "queue_claim" || m === "queue_offer" || m === "queue_claimed") return "queue_claim";
  if (m === "queue_auto") return "queue_auto";
  if (m === "live_join") return "live_join";
  if (m === "inbound_answer" || m === "softphone_dial" || m === "transfer") return "inbound_answer";
  return null;
}

function voiceLogPayloadExtra(attendedMode, extra = null) {
  const base = extra && typeof extra === "object" ? { ...extra } : {};
  if (attendedMode) base.attendedMode = attendedMode;
  return Object.keys(base).length ? base : undefined;
}

async function reportVoicePresence(channelId, payload) {
  if (!channelId) return;
  try {
    await updateTwilioVoicePresence(channelId, payload || {});
  } catch (err) {
    console.warn("[CallContext] voice presence:", err?.message || err);
  }
}

function readStoredAnswerMode() {
  try {
    const raw = localStorage.getItem("xpro_voice_answer_mode");
    const m = String(raw || "").trim().toLowerCase();
    if (m === "softphone" || m === "pstn" || m === "auto") return m;
  } catch (_) {
    /* ignore */
  }
  return "auto";
}

/** Best-effort em pagehide/unload (headers + keepalive). */
function clearDeviceErrorNoticeTimer(timersRef, channelId) {
  const timers = timersRef.current;
  if (!timers) return;
  if (channelId == null) {
    timers.forEach((id) => clearTimeout(id));
    timers.clear();
    return;
  }
  const id = timers.get(channelId);
  if (id) {
    clearTimeout(id);
    timers.delete(channelId);
  }
}

function keepaliveVoicePost(channelId, pathSuffix, payload) {
  const cid = Number(channelId) || 0;
  if (!cid) return;
  try {
    const token = readTabBearerToken();
    const company = getActiveWorkspaceSlug();
    const base = String(getApiBaseURL() || "").replace(/\/+$/, "");
    const url = `${base}/channels/${cid}/${pathSuffix}`;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (company) headers.Company = company;
    void fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload || {}),
      keepalive: true,
      credentials: "omit",
    });
  } catch (err) {
    console.warn("[CallContext] keepalive:", err?.message || err);
  }
}

export const CallProvider = ({ children }) => {
  const [device, setDevice] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [outgoingCall, setOutgoingCall] = useState(null); 
  const [status, setStatus] = useState("Disconnected");
  const [mockMode, setMockMode] = useState(false); // Ative o modo de simulação
  const [audioPermission, setAudioPermission] = useState(false); // Permissão para áudio
  /** Lead sendo conectado após "Falar agora" na fila (auto-aceite). */
  const [connectingToLead, setConnectingToLead] = useState(null);
  const [softphoneError, setSoftphoneError] = useState(null);
  const [lastCallNotice, setLastCallNotice] = useState(null);
  const deviceReconnectRef = useRef(false);
  const deviceErrorNoticeTimersRef = useRef(new Map());
  const devicesByChannelRef = useRef(new Map());
  const pendingSoftphoneRef = useRef(null);
  /** { leadId, channelId, callSid, until } — auto-aceita a próxima incoming da fila. */
  const pendingAutoAcceptRef = useRef(null);
  const outgoingCallRef = useRef(null);
  const incomingCallRef = useRef(null);
  const voiceTabIdRef = useRef(
    `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );
  const softphonePresenceChannelRef = useRef(null);
  const softphoneReadyRef = useRef(false);
  const answerModeRef = useRef("auto");
  const wakeLockRef = useRef(null);
  const hangupByUserRef = useRef(false);
  /** Sempre a implementação atual; a identidade exportada permanece estável. */
  const setupTwilioDeviceRef = useRef(null);
  const acceptIncomingTwilioCallRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    outgoingCallRef.current = outgoingCall;
  }, [outgoingCall]);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  useEffect(() => {
    return () => {
      clearDeviceErrorNoticeTimer(deviceErrorNoticeTimersRef);
    };
  }, []);

  // Avisa antes de reload/fechar com softphone ativo nesta aba.
  useEffect(() => {
    const onBeforeUnload = (ev) => {
      const active =
        outgoingCallRef.current?.callInstance ||
        (outgoingCallRef.current && outgoingCallRef.current.mode === "mock") ||
        incomingCall;
      if (!active) return;
      ev.preventDefault();
      ev.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [incomingCall]);

  // Queda acidental: marca technical_drop + libera presença (Twilio Dial action completa o fluxo do cliente).
  useEffect(() => {
    const onPageHide = () => {
      if (hangupByUserRef.current) return;
      const current = outgoingCallRef.current;
      if (!current?.callInstance && current?.mode !== "mock") return;
      const channelId = Number(current?.channelId || 0);
      const leadId = Number(current?.leadId || 0);
      const params = current?.callInstance?.parameters || {};
      const parentSid = String(params.ParentCallSid || current?.parentCallSid || "").trim();
      const thisSid = String(params.CallSid || current?.callSid || "").trim();
      const callSid = parentSid || thisSid;
      if (!channelId || !leadId || !callSid) return;
      const isInbound =
        current?.direction === "inbound" || current?.mode === "inbound";
      const durationSec = current?.startTime
        ? Math.max(0, Math.round((Date.now() - new Date(current.startTime).getTime()) / 1000))
        : null;
      keepaliveVoicePost(channelId, "voice/call-log", {
        leadId,
        callSid,
        status: "technical-drop",
        answerOutcome: "technical_drop",
        endedBy: "system",
        direction: isInbound ? "inbound" : "outbound",
        durationSec,
        source: "client_sdk",
        code: "window_unload",
      });
      keepaliveVoicePost(channelId, "twilio/voice-presence", {
        status: "available",
        softphoneReady: false,
        callSid,
        leadId,
      });
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  const reportCallEndedBy = useCallback(async (endedBy, callState = null) => {
    const current = callState || outgoingCallRef.current;
    const channelId = Number(current?.channelId || 0);
    const leadId = Number(current?.leadId || 0);
    const params = current?.callInstance?.parameters || {};
    const parentSid = String(params.ParentCallSid || current?.parentCallSid || "").trim();
    const thisSid = String(params.CallSid || current?.callSid || "").trim();
    const callSid = parentSid || thisSid;
    if (!channelId || !leadId || !callSid) return;
    const isInbound =
      current?.direction === "inbound" ||
      current?.mode === "inbound" ||
      Boolean(resolveAttendedModeFromCall(params, current));
    const attendedMode = resolveAttendedModeFromCall(params, current);
    const durationSec = current?.startTime
      ? Math.max(0, Math.round((Date.now() - new Date(current.startTime).getTime()) / 1000))
      : null;
    try {
      await recordVoiceCallLog(channelId, {
        leadId,
        callSid,
        status: "completed",
        endedBy,
        direction: isInbound ? "inbound" : "outbound",
        to: isInbound ? "" : current?.toNumber || "",
        from: isInbound ? current?.toNumber || params.From || "" : "",
        durationSec,
        source: "client_sdk",
        attendedMode: attendedMode || undefined,
        extra: voiceLogPayloadExtra(attendedMode),
      });
    } catch (err) {
      console.warn("[CallContext] reportCallEndedBy:", err?.message || err);
    }
  }, []);

  const attachCallDisconnectHandlers = useCallback(
    (call, callState) => {
      if (!call?.on) return;
      call.on("disconnect", () => {
        const endedByUser = hangupByUserRef.current;
        hangupByUserRef.current = false;
        if (!endedByUser) {
          void reportCallEndedBy("client", callState || outgoingCallRef.current);
        }
        const chId =
          Number(callState?.channelId || outgoingCallRef.current?.channelId || 0) || null;
        void reportVoicePresence(chId, { status: "available" });
        const ended = callState || outgoingCallRef.current;
        const elapsed = ended?.startTime
          ? Date.now() - new Date(ended.startTime).getTime()
          : 0;
        if (!endedByUser && elapsed > 0 && elapsed < 5000) {
          setLastCallNotice({
            status: "failed",
            reason: "A ligação encerrou em poucos segundos.",
            toNumber: ended?.toNumber || "",
          });
        }
        setOutgoingCall(null);
        setStatus("Ready");
      });
    },
    [reportCallEndedBy]
  );

  // Espelha a ligação ativa para /popup-call (e responde sync).
  useEffect(() => {
    if (outgoingCall) {
      publishActiveCallSession(serializeOutgoingCall(outgoingCall));
    } else {
      publishCallCleared();
    }
  }, [outgoingCall]);

  useEffect(() => {
    return subscribeCallSessionBridge((msg) => {
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "action" && msg.action === "request_sync") {
        const current = outgoingCallRef.current;
        if (current) publishActiveCallSession(serializeOutgoingCall(current));
        return;
      }
      if (msg.type !== "action") return;
      const current = outgoingCallRef.current;
      if (!current) return;
      const ownsSoftphone =
        Boolean(current.callInstance) ||
        current.mode === "mock" ||
        current.mode === "twilio_pstn" ||
        current.canHangupRemote;
      if (!ownsSoftphone) return;

      if (msg.action === "hangup") {
        hangupByUserRef.current = true;
        void (async () => {
          try {
            await reportCallEndedBy("user", current);
          } catch (_) {
            /* ignore */
          }
          void reportVoicePresence(Number(current?.channelId || 0) || null, {
            status: "available",
          });
          if (
            (current.mode === "twilio_pstn" || current.canHangupRemote) &&
            current.callSid &&
            current.channelId
          ) {
            try {
              await hangupTwilioCall(Number(current.channelId), {
                callSid: current.callSid,
                leadId: current.leadId || undefined,
              });
            } catch (err) {
              console.warn("[CallContext] bridge hangupTwilioCall:", err?.message || err);
            }
            setOutgoingCall(null);
            setStatus("Disconnected");
            return;
          }
          try {
            current.callInstance?.disconnect?.();
          } catch (_) {
            /* ignore */
          }
          if (current.mode === "mock" || !current.callInstance) {
            setOutgoingCall(null);
            setStatus("Disconnected");
          }
        })();
        return;
      }
      if (msg.action === "dtmf") {
        const raw = String(msg.payload?.digits ?? "").replace(/[^0-9*#wW]/g, "");
        if (!raw) return;
        if (current.mode === "mock") return;
        try {
          current.callInstance?.sendDigits?.(raw);
        } catch (err) {
          console.warn("[CallContext] bridge dtmf:", err?.message || err);
        }
      }
    });
  }, [reportCallEndedBy]);

  const rememberSoftphoneIncoming = useCallback((payload = {}) => {
    const leadId = Number(payload.leadId || payload.chatId || payload.protocol || 0) || null;
    const channelId = Number(payload.channelId || 0) || null;
    if (!leadId && !channelId) return;
    pendingSoftphoneRef.current = {
      leadId,
      channelId,
      at: Date.now(),
    };
  }, []);

  /**
   * Após "Falar agora" na fila: a próxima incoming do softphone é aceita sozinha
   * (o atendente já pediu a ligação — sem segundo clique em Aceitar).
   */
  const armSoftphoneAutoAccept = useCallback((payload = {}) => {
    const leadId = Number(payload.leadId || 0) || null;
    const channelId = Number(payload.channelId || 0) || null;
    const callSid = String(payload.callSid || "").trim() || null;
    if (!leadId && !channelId && !callSid) return;
    pendingAutoAcceptRef.current = {
      leadId,
      channelId,
      callSid,
      until: Date.now() + 45_000,
    };
    rememberSoftphoneIncoming({ leadId, channelId });
    if (leadId) setConnectingToLead(leadId);
  }, [rememberSoftphoneIncoming]);

  const clearSoftphoneAutoAccept = useCallback(() => {
    pendingAutoAcceptRef.current = null;
    setConnectingToLead(null);
  }, []);

  /** Após "Falar agora" ter sucesso: marca ocupado no servidor (além do backend do claim). */
  const markBusyForQueueClaim = useCallback((payload = {}) => {
    const channelId = Number(payload.channelId || 0) || null;
    const leadId = Number(payload.leadId || 0) || null;
    const callSid = String(payload.callSid || "").trim() || null;
    if (leadId) setConnectingToLead(leadId);
    if (!channelId) return;
    void reportVoicePresence(channelId, {
      status: "on_call",
      callSid,
      leadId,
      heartbeat: true,
      softphoneReady: Boolean(softphoneReadyRef.current),
    });
  }, []);

  const matchesPendingAutoAccept = useCallback((call, leadFromCall, callSid) => {
    const pending = pendingAutoAcceptRef.current;
    if (!pending || Date.now() > pending.until) {
      if (pending) pendingAutoAcceptRef.current = null;
      return false;
    }
    const attendedMode = resolveAttendedModeFromCall(call?.parameters);
    // Só auto-aceita claim manual (usuário clicou "Falar agora").
    if (attendedMode === "queue_claim") return true;
    const pendingLead = Number(pending.leadId || 0);
    const callLead = Number(leadFromCall || 0);
    if (pendingLead > 0 && callLead > 0 && pendingLead === callLead) return true;
    if (pending.callSid && callSid && String(pending.callSid) === String(callSid)) return true;
    return false;
  }, []);

  // Detecta interação inicial do usuário
  useEffect(() => {
    const handleUserInteraction = () => {
      setAudioPermission(true); // Ativa permissão para reprodução de áudio
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };

    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);

    return () => {
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };
  }, []);

  const setupTwilioDevice = async (channelId, leadId = null, { forceRefresh = false } = {}) => {
    if (mockMode) return null;
    if (!channelId) return null;
    const cid = Number(channelId);
    if (leadId) {
      rememberSoftphoneIncoming({ leadId, channelId: cid });
    }

    const existing = devicesByChannelRef.current.get(cid);
    if (existing && !forceRefresh) {
      setDevice(existing);
      return existing;
    }
    if (device && device.__xproChannelId === cid && !forceRefresh) return device;

    if (forceRefresh && existing) {
      existing.__xproDestroyed = true;
      devicesByChannelRef.current.delete(cid);
      clearDeviceErrorNoticeTimer(deviceErrorNoticeTimersRef, cid);
      try {
        existing.destroy?.();
      } catch (_) {
        /* ignore */
      }
    }

    const tokenResp = await getTwilioVoiceToken(
      channelId,
      leadId ? { leadId: Number(leadId) } : {}
    );
    const token = tokenResp?.token;
    if (!token) throw new Error("Token de voz inválido.");

    const d = new Device(token, { codecPreferences: ["opus", "pcmu"] });
    d.__xproChannelId = cid;
    d.__xproTokenAt = Date.now();
    devicesByChannelRef.current.set(cid, d);

    const isLiveDevice = () =>
      !d.__xproDestroyed && devicesByChannelRef.current.get(cid) === d;

    const deviceStateOf = () => {
      try {
        return String(d?.state || "").toLowerCase();
      } catch (_) {
        return "";
      }
    };

    d.on("registered", () => {
      if (!isLiveDevice()) return;
      clearDeviceErrorNoticeTimer(deviceErrorNoticeTimersRef, cid);
      setStatus("Ready");
      setSoftphoneError(null);
    });
    d.on("unregistered", () => {
      if (!isLiveDevice()) return;
      softphoneReadyRef.current = false;
      void reportVoicePresence(cid, {
        status: outgoingCallRef.current ? "on_call" : "available",
        softphoneReady: false,
        heartbeat: true,
      });
    });
    d.on("error", (error) => {
      if (!isLiveDevice()) return;
      const code = twilioErrorCode(error) || null;
      const rawMessage = twilioErrorRawMessage(error) || "Softphone desconectado";
      const tokenExpired = isTwilioAccessTokenError(code, rawMessage);
      const transient = isTwilioTransientConnectionError(code, rawMessage);

      if (tokenExpired) {
        console.warn("[CallContext] Device token:", code, rawMessage);
        if (!deviceReconnectRef.current) {
          deviceReconnectRef.current = true;
          setupTwilioDeviceRef.current?.(cid, null, { forceRefresh: true })
            .catch((err) => {
              const failRaw = String(err?.message || err || rawMessage);
              const message =
                humanizeVoiceCallError(failRaw, { code: code || 31205, scope: "device" }) ||
                failRaw;
              setSoftphoneError({ code: code || 31205, message, rawMessage: failRaw });
            })
            .finally(() => {
              deviceReconnectRef.current = false;
            });
        }
        return;
      }

      // 31005/ICE no Device ocioso: o SDK costuma recuperar. Recriar o Device
      // emite outro HANGUP e abre o painel vermelho sem ligação em curso.
      if (transient) {
        console.warn("[CallContext] Device connection:", code, rawMessage);
        const timers = deviceErrorNoticeTimersRef.current;
        if (!timers.has(cid)) {
          timers.set(
            cid,
            setTimeout(() => {
              timers.delete(cid);
              if (!isLiveDevice()) return;
              if (
                !shouldSurfaceTwilioDeviceError(error, {
                  deviceState: deviceStateOf(),
                  destroyed: Boolean(d.__xproDestroyed),
                  isCurrentDevice: true,
                })
              ) {
                return;
              }
              const message =
                humanizeVoiceCallError(rawMessage, { code, scope: "device" }) || rawMessage;
              setSoftphoneError({ code, message, rawMessage });
              softphoneReadyRef.current = false;
              void reportVoicePresence(cid, {
                status: outgoingCallRef.current ? "on_call" : "available",
                softphoneReady: false,
                heartbeat: true,
              });
            }, 8000)
          );
        }
        return;
      }

      console.error("Device error:", error);
      const message =
        humanizeVoiceCallError(rawMessage, { code, scope: "device" }) || rawMessage;
      setSoftphoneError({ code, message, rawMessage });
      softphoneReadyRef.current = false;
      void reportVoicePresence(cid, {
        status: outgoingCallRef.current ? "on_call" : "available",
        softphoneReady: false,
        heartbeat: true,
      });
    });
    d.on("tokenWillExpire", () => {
      if (!isLiveDevice()) return;
      setupTwilioDeviceRef.current?.(cid, null, { forceRefresh: true }).catch((err) => {
        console.warn("[CallContext] token refresh:", err?.message || err);
      });
    });
    d.on("incoming", (call) => {
      if (!isLiveDevice()) return;
      const leadFromCall = resolveLeadIdFromIncoming(call, pendingSoftphoneRef.current);
      if (leadFromCall) {
        call.__xproLeadId = leadFromCall;
      }
      call.__xproRingStartedAt = new Date().toISOString();
      setDevice(d);

      const channelId =
        Number(call?.parameters?.ChannelId || d?.__xproChannelId || pendingSoftphoneRef.current?.channelId || 0) ||
        null;
      const parentSid = String(call?.parameters?.ParentCallSid || "").trim();
      const thisSid = String(call?.parameters?.CallSid || "").trim();
      const callSid = parentSid || thisSid;
      const attendedMode = resolveAttendedModeFromCall(call?.parameters);

      if (channelId && leadFromCall && callSid) {
        void logVoiceAttempt(channelId, {
          leadId: leadFromCall,
          callSid,
          status: "ringing",
          direction: "inbound",
          from: call?.parameters?.From || "",
          to: "",
          ringStartedAt: call.__xproRingStartedAt,
          source: "client_sdk",
          attendedMode,
          extra: voiceLogPayloadExtra(attendedMode),
        });
      }

      // Fila: usuário já clicou "Falar agora" → conecta sem pedir Aceitar de novo.
      if (matchesPendingAutoAccept(call, leadFromCall, callSid)) {
        pendingAutoAcceptRef.current = null;
        void acceptIncomingTwilioCallRef.current?.(call);
        return;
      }

      setIncomingCall(call);
      startIncomingCallAttention({
        title: "Ligação entrando",
        body: leadFromCall
          ? `Atenda a solicitação #${leadFromCall} no Xpro.`
          : "Há uma ligação no Xpro.",
        url: leadFromCall ? `/chat?open=${leadFromCall}&voice=1` : "/chat?voice=1",
        tag: `voice-in-${leadFromCall || callSid || "ring"}`,
      });
      publishIncomingRing({
        fromTabId: voiceTabIdRef.current,
        leadId: leadFromCall,
        channelId,
        callSid,
        from: call?.parameters?.From || "",
      });
      if (typeof call.on === "function") {
        call.on("cancel", () => {
          stopIncomingCallAttention();
          publishIncomingCleared({ fromTabId: voiceTabIdRef.current, callSid });
          setIncomingCall(null);
        });
      }
    });

    try {
      await d.register();
    } catch (err) {
      d.__xproDestroyed = true;
      if (devicesByChannelRef.current.get(cid) === d) {
        devicesByChannelRef.current.delete(cid);
      }
      clearDeviceErrorNoticeTimer(deviceErrorNoticeTimersRef, cid);
      throw err;
    }
    setDevice(d);
    softphonePresenceChannelRef.current = cid;
    const ready = shouldReportSoftphoneReady({
      hasRegisteredDevice: true,
      visibilityState:
        typeof document === "undefined" ? "visible" : document.visibilityState,
      deviceClass: detectDeviceClass(),
    });
    softphoneReadyRef.current = ready;
    void reportVoicePresence(cid, {
      status: "available",
      softphoneReady: ready,
      answerMode: answerModeRef.current || readStoredAnswerMode(),
      deviceClass: detectDeviceClass(),
      heartbeat: true,
    });
    return d;
  };
  setupTwilioDeviceRef.current = setupTwilioDevice;

  const prepareTwilioVoiceDevice = useCallback((channelId, leadId, options) => {
    return setupTwilioDeviceRef.current?.(channelId, leadId, options);
  }, []);

  const setVoiceAnswerMode = useCallback((mode) => {
    const m = String(mode || "").trim().toLowerCase();
    const next = m === "softphone" || m === "pstn" ? m : "auto";
    answerModeRef.current = next;
    try {
      localStorage.setItem("xpro_voice_answer_mode", next);
    } catch (_) {
      /* ignore */
    }
    const cid = softphonePresenceChannelRef.current;
    if (cid) {
      void reportVoicePresence(cid, {
        status: "available",
        softphoneReady: softphoneReadyRef.current,
        answerMode: next,
        deviceClass: detectDeviceClass(),
        heartbeat: true,
      });
    }
  }, []);

  // Heartbeat: no computador o Device segue pronto em segundo plano / outra aba.
  // No celular, softphone_ready só com aba visível (WebRTC morre em background).
  useEffect(() => {
    if (mockMode) return undefined;
    answerModeRef.current = readStoredAnswerMode();

    const pushPresence = (ready, { heartbeat = false } = {}) => {
      softphoneReadyRef.current = Boolean(ready);
      const cid = softphonePresenceChannelRef.current;
      if (!cid && devicesByChannelRef.current.size) {
        softphonePresenceChannelRef.current = Number(
          devicesByChannelRef.current.keys().next().value
        );
      }
      const channelId = softphonePresenceChannelRef.current;
      if (!channelId) return;
      void reportVoicePresence(channelId, {
        status: outgoingCallRef.current ? "on_call" : "available",
        softphoneReady: Boolean(ready),
        answerMode: answerModeRef.current || readStoredAnswerMode(),
        deviceClass: detectDeviceClass(),
        heartbeat: Boolean(heartbeat),
        callSid: outgoingCallRef.current?.callSid || null,
        leadId: outgoingCallRef.current?.leadId || null,
      });
    };

    const reportCurrentReady = ({ heartbeat = true } = {}) => {
      const ready = shouldReportSoftphoneReady({
        hasRegisteredDevice: devicesByChannelRef.current.size > 0,
        visibilityState:
          typeof document === "undefined" ? "visible" : document.visibilityState,
        deviceClass: detectDeviceClass(),
      });
      pushPresence(ready, { heartbeat });
    };

    const onVisibility = () => {
      reportCurrentReady({ heartbeat: true });
    };

    const onPageHide = () => {
      // Fecha a janela: esta aba deixa de estar pronta. Outra janela aberta segue no heartbeat.
      pushPresence(false, { heartbeat: false });
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    const HEARTBEAT_MS = 12_000;
    const timer = setInterval(() => {
      if (!devicesByChannelRef.current.size) return;
      reportCurrentReady({ heartbeat: true });
    }, HEARTBEAT_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      clearInterval(timer);
    };
  }, [mockMode]);

  // Wake Lock enquanto há chamada ativa (evita tela dormir no PWA).
  useEffect(() => {
    const active = Boolean(outgoingCall || incomingCall);
    let cancelled = false;

    const release = async () => {
      const lock = wakeLockRef.current;
      wakeLockRef.current = null;
      if (lock) {
        try {
          await lock.release();
        } catch (_) {
          /* ignore */
        }
      }
    };

    const request = async () => {
      if (!active || cancelled) return;
      if (typeof navigator === "undefined" || !navigator.wakeLock?.request) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch (err) {
        console.warn("[CallContext] wakeLock:", err?.message || err);
      }
    };

    if (active) {
      void request();
    } else {
      void release();
    }

    const onVis = () => {
      if (document.visibilityState === "visible" && active) void request();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      void release();
    };
  }, [outgoingCall, incomingCall]);

  // Fallback: renova tokens ~45min (SDK nem sempre dispara tokenWillExpire).
  useEffect(() => {
    if (mockMode) return undefined;
    const REFRESH_MS = 45 * 60 * 1000;
    const timer = setInterval(() => {
      const entries = Array.from(devicesByChannelRef.current.entries());
      entries.forEach(([cid, d]) => {
        const age = Date.now() - Number(d?.__xproTokenAt || 0);
        if (age < REFRESH_MS - 60_000) return;
        setupTwilioDeviceRef.current?.(cid, null, { forceRefresh: true }).catch((err) => {
          console.warn("[CallContext] scheduled refresh:", cid, err?.message || err);
        });
      });
    }, 60_000);
    return () => clearInterval(timer);
  }, [mockMode]);

  // Outras janelas (Chrome / app / escritório virtual): tocam e podem aceitar via a aba do Device.
  useEffect(() => {
    if (mockMode) return undefined;
    return subscribeCallSessionBridge((msg) => {
      if (!msg || typeof msg !== "object") return;
      const myTab = voiceTabIdRef.current;
      if (msg.type === "incoming_ring") {
        const p = msg.payload || {};
        if (p.fromTabId && p.fromTabId === myTab) return;
        if (incomingCallRef.current && !incomingCallRef.current.__xproMirrored) return;
        const leadId = Number(p.leadId || 0) || null;
        const channelId = Number(p.channelId || 0) || null;
        const callSid = String(p.callSid || "").trim() || null;
        startIncomingCallAttention({
          title: "Ligação entrando",
          body: leadId
            ? `Atenda a solicitação #${leadId} no Xpro.`
            : "Há uma ligação no Xpro.",
          url: leadId ? `/chat?open=${leadId}&voice=1` : "/chat?voice=1",
          tag: `voice-in-${leadId || callSid || "ring"}`,
        });
        setIncomingCall({
          __xproMirrored: true,
          __xproLeadId: leadId,
          parameters: {
            From: p.from || "",
            LeadId: leadId,
            ChannelId: channelId,
            ParentCallSid: callSid,
            CallSid: callSid,
          },
          accept: async () => {
            publishCallAction("accept_incoming", { fromTabId: p.fromTabId, callSid });
          },
          reject: async () => {
            publishCallAction("reject_incoming", { fromTabId: p.fromTabId, callSid });
          },
        });
        return;
      }
      if (msg.type === "incoming_cleared") {
        stopIncomingCallAttention();
        const current = incomingCallRef.current;
        if (current?.__xproMirrored) setIncomingCall(null);
        return;
      }
      if (msg.type === "action" && msg.action === "accept_incoming") {
        const current = incomingCallRef.current;
        if (!current || current.__xproMirrored) return;
        void acceptIncomingTwilioCallRef.current?.(current);
        return;
      }
      if (msg.type === "action" && msg.action === "reject_incoming") {
        const current = incomingCallRef.current;
        if (!current || current.__xproMirrored) return;
        if (typeof current.reject === "function") {
          void current.reject();
        }
        stopIncomingCallAttention();
        publishIncomingCleared({ fromTabId: myTab });
        setIncomingCall(null);
      }
    });
  }, [mockMode]);

  // Simulação de chamadas no modo mock
  useEffect(() => {
    if (mockMode) {
      const simulateIncomingCall = setInterval(() => {
        setIncomingCall({
          parameters: { From: "+123456789", Protocol: "ABCD12345" },
          accept: () => {
            setStatus("In Call");
            setIncomingCall(null);
            setOutgoingCall(true);
            navigate("/chat");
          },
          reject: () => {
            setIncomingCall(null);
          },
        });
        startIncomingCallRingtone();
      }, 15000); // Simula uma chamada a cada 15 segundos

      return () => {
        clearInterval(simulateIncomingCall);
        stopIncomingCallRingtone();
      };
    }
  }, [mockMode, navigate]);

  // Gerencia o início de uma ligação efetuada
  const startOutgoingCall = async (params) => {
    const rawTo = typeof params === "string" ? params : params?.toNumber;
    const channelId = typeof params === "object" ? params?.channelId : null;
    const leadId = typeof params === "object" ? params?.leadId : null;
    const rawFrom = typeof params === "object" ? params?.fromNumber : null;
    const ramal = typeof params === "object" ? params?.ramal : null;
    const mode = typeof params === "object" ? String(params?.mode || "").toLowerCase() : "";

    const toNorm = normalizeDialPhone(rawTo);
    const toNumber = toNorm.ok ? toNorm.e164 : String(rawTo || "").trim();
    const fromNorm = rawFrom ? normalizeDialPhone(rawFrom) : null;
    const fromNumber = fromNorm?.ok ? fromNorm.e164 : rawFrom || null;

    if (mockMode) {
        setOutgoingCall({
            toNumber,
            startTime: new Date(),
            channelId,
            leadId,
            mode: mode || "mock",
            end: () => {
              setOutgoingCall(null);
              setStatus("Disconnected");
            },
          });
          setStatus("Calling");
    } else {
        if (!channelId || !toNumber) {
          console.error("Parâmetros obrigatórios ausentes para originar chamada (channelId/toNumber).");
          return;
        }
        if (!toNorm.ok) {
          await logVoiceAttempt(channelId, {
            leadId,
            to: String(rawTo || ""),
            from: fromNumber || "",
            status: "failed",
            failureReason: toNorm.error || "Número de destino inválido",
            source: mode === "pabx" ? "pabx" : "client_sdk",
            direction: "outbound",
          });
          throw new Error(toNorm.error || "Número de destino inválido.");
        }

        // Telefonia (PABX / central do cliente): HTTP na URL configurada no canal.
        if (mode === "pabx") {
          try {
            setStatus("Calling");
            const data = await originatePabxCall(channelId, {
              toNumber,
              fromNumber: fromNumber || undefined,
              ramal: ramal || undefined,
              leadId: leadId || undefined,
            });
            setOutgoingCall({
              toNumber,
              fromNumber: fromNumber || null,
              startTime: new Date(),
              channelId,
              leadId,
              mode: "pabx",
              callSid: data?.callSid || null,
              // Sem softphone: encerra estado local; a central controla a chamada.
              callInstance: null,
            });
            setStatus("In Call");
          } catch (error) {
            console.error("Erro ao originar chamada PABX:", error);
            await logVoiceAttempt(channelId, {
              leadId,
              to: toNumber,
              from: fromNumber || "",
              status: "failed",
              failureReason:
                error?.response?.data?.message || error?.message || "Falha ao originar PABX",
              source: "pabx",
              direction: "outbound",
            });
            setOutgoingCall(null);
            setStatus("Disconnected");
            throw error;
          }
          return;
        }

        // Origem = telefone (não softphone): click-to-call Twilio (2 pernas).
        if (mode === "twilio_pstn" || mode === "click_to_call") {
          if (!fromNumber) {
            throw new Error("Informe o número de origem (telefone) para esta ligação.");
          }
          try {
            setStatus("Calling");
            const data = await createTwilioClickToCall(channelId, {
              to: toNumber,
              agentPhone: fromNumber,
              leadId: leadId || undefined,
            });
            const callSid = data?.callSid || data?.sid || null;
            const next = {
              toNumber,
              fromNumber,
              startTime: new Date(),
              channelId,
              leadId,
              mode: "twilio_pstn",
              direction: "outbound",
              bridgeMode: "click_to_call",
              callSid,
              callInstance: null,
              canHangupRemote: true,
            };
            hangupByUserRef.current = false;
            outgoingCallRef.current = next;
            setOutgoingCall(next);
            setStatus("In Call");
            void reportVoicePresence(channelId, {
              status: "on_call",
              callSid,
              leadId: leadId || null,
            });
          } catch (error) {
            console.error("Erro ao originar click-to-call:", error);
            await logVoiceAttempt(channelId, {
              leadId,
              to: toNumber,
              from: fromNumber || "",
              status: "failed",
              failureReason:
                error?.response?.data?.message || error?.message || "Falha no click-to-call",
              source: "click_to_call",
              direction: "outbound",
            });
            setOutgoingCall(null);
            setStatus("Disconnected");
            throw error;
          }
          return;
        }
      
        try {
          const d = await setupTwilioDevice(channelId, leadId);
          if (!d) throw new Error("Softphone do navegador não configurado para este canal.");
          const company = getActiveWorkspaceSlug() || "";
          const voiceChannelId = d.__xproChannelId || channelId;
          const call = await d.connect({
            params: {
              To: toNumber,
              From: fromNumber || "",
              ChannelId: String(voiceChannelId),
              LeadId: leadId ? String(leadId) : "",
              // TwiML App URL compartilhada exige company no POST (gate multi-tenant da API).
              ...(company ? { company } : {}),
            },
          });

          setOutgoingCall({
            toNumber,
            startTime: new Date(),
            channelId,
            leadId,
            mode: "twilio",
            direction: "outbound",
            callInstance: call,
            callSid: call?.parameters?.CallSid || null,
          });
      
          setStatus("In Call");
          hangupByUserRef.current = false;
          attachCallDisconnectHandlers(call, {
            toNumber,
            startTime: new Date(),
            channelId,
            leadId,
            mode: "twilio",
            direction: "outbound",
            callInstance: call,
            callSid: call?.parameters?.CallSid || null,
          });
          void reportVoicePresence(channelId, {
            status: "on_call",
            callSid: call?.parameters?.CallSid || null,
            leadId: leadId || null,
          });
          call.on("error", (callErr) => {
            console.error("Erro na chamada Twilio:", callErr);
            const code = Number(callErr?.code || callErr?.twilioError?.code || 0) || null;
            const rawReason = callErr?.message || String(callErr);
            const reason =
              humanizeVoiceCallError(rawReason, { code, status: "failed" }) || rawReason;
            logVoiceAttempt(channelId, {
              leadId,
              to: toNumber,
              from: fromNumber || "",
              callSid: call?.parameters?.CallSid || undefined,
              status: "failed",
              failureReason: reason,
              source: "client_sdk",
              direction: "outbound",
            });
            setLastCallNotice({
              status: "failed",
              reason,
              code,
              toNumber,
            });
            void reportVoicePresence(channelId, { status: "available" });
          });
        } catch (error) {
          console.error("Erro ao iniciar a ligação:", error);
          const code = Number(error?.code || error?.twilioError?.code || 0) || null;
          const rawReason = error?.message || String(error);
          const reason =
            humanizeVoiceCallError(rawReason, { code, status: "client_error" }) || rawReason;
          await logVoiceAttempt(channelId, {
            leadId,
            to: toNumber,
            from: fromNumber || "",
            status: "client_error",
            failureReason: reason,
            source: "client_sdk",
            direction: "outbound",
          });
          setLastCallNotice({
            status: "failed",
            reason,
            code,
            toNumber,
          });
          void reportVoicePresence(channelId, { status: "available" });
          setOutgoingCall(null);
          setStatus("Disconnected");
          throw error;
        }
    }
  };  

  const joinLiveVoiceCall = useCallback(
    async ({ channelId, leadId, callSid } = {}) => {
      const cid = Number(channelId);
      const lid = Number(leadId);
      const sid = String(callSid || "").trim();
      if (!cid || !lid || !sid) {
        throw new Error("Dados insuficientes para entrar na ligação.");
      }

      const current = outgoingCallRef.current;
      if (current?.callInstance || current?.mode === "mock") {
        throw new Error("Encerre a ligação atual antes de entrar em outra.");
      }

      const data = await joinVoiceLiveCallApi(cid, { leadId: lid, callSid: sid });
      const conferenceName = String(data?.conferenceName || "").trim();
      if (!conferenceName) {
        throw new Error("Não foi possível preparar a conferência.");
      }

      const d = await setupTwilioDeviceRef.current?.(cid, lid);
      if (!d) throw new Error("Softphone não configurado para este canal.");

      const company = getActiveWorkspaceSlug() || "";
      const call = await d.connect({
        params: {
          To: `conference:${conferenceName}`,
          ChannelId: String(cid),
          LeadId: String(lid),
          CallSid: sid,
          ...(company ? { company } : {}),
        },
      });

      const next = {
        toNumber: "Conferência",
        startTime: new Date(),
        channelId: cid,
        leadId: lid,
        mode: "twilio",
        direction: "inbound",
        attendedMode: "live_join",
        callInstance: call,
        callSid: sid,
        parentCallSid: sid,
        conferenceName,
        isLiveJoin: true,
      };
      hangupByUserRef.current = false;
      outgoingCallRef.current = next;
      setOutgoingCall(next);
      setStatus("In Call");
      attachCallDisconnectHandlers(call, next);
      void reportVoicePresence(cid, {
        status: "on_call",
        callSid: sid,
        leadId: lid,
      });
      return data;
    },
    [attachCallDisconnectHandlers]
  );

  const endOutgoingCall = async () => {
    hangupByUserRef.current = true;
    const current = outgoingCallRef.current || outgoingCall;
    try {
      await reportCallEndedBy("user", current);
    } catch (_) {
      /* ignore */
    }
    const chId = Number(current?.channelId || 0) || null;
    void reportVoicePresence(chId, { status: "available" });
    if (mockMode) {
      setOutgoingCall(null);
      setStatus("Disconnected");
      return;
    }
    // Click-to-call / REST: hangup via API (não há Call no navegador).
    if (
      (current?.mode === "twilio_pstn" || current?.canHangupRemote) &&
      current?.callSid &&
      chId
    ) {
      try {
        await hangupTwilioCall(chId, {
          callSid: current.callSid,
          leadId: current.leadId || undefined,
        });
      } catch (err) {
        console.warn("[CallContext] hangupTwilioCall:", err?.message || err);
      }
      setOutgoingCall(null);
      setStatus("Disconnected");
      return;
    }
    try {
      current?.callInstance?.disconnect?.();
    } catch (_) {
      /* ignore */
    }
    setOutgoingCall(null);
    setStatus("Disconnected");
  };

  /**
   * Envia DTMF na ligação ativa (URA / menus). Softphone Twilio: Call.sendDigits.
   * @param {string} digits - 0-9, *, # (e opcionalmente 'w' = pause)
   */
  const sendOutgoingDtmf = (digits) => {
    const raw = String(digits ?? "").replace(/[^0-9*#wW]/g, "");
    if (!raw) return { ok: false, reason: "empty" };
    if (mockMode) {
      return { ok: true, mock: true, digits: raw };
    }
    const call = outgoingCall?.callInstance;
    if (!call || typeof call.sendDigits !== "function") {
      return { ok: false, reason: "no_active_softphone" };
    }
    try {
      call.sendDigits(raw);
      return { ok: true, digits: raw };
    } catch (err) {
      console.warn("[CallContext] sendDigits:", err?.message || err);
      return { ok: false, reason: err?.message || "send_failed" };
    }
  };

  const acceptIncomingTwilioCall = useCallback(async (call) => {
    if (!call) return null;
    if (call.__xproMirrored) {
      if (typeof call.accept === "function") await call.accept();
      return null;
    }
    const leadId =
      Number(call?.__xproLeadId || 0) ||
      resolveLeadIdFromIncoming(call, pendingSoftphoneRef.current);
    const ringStartedAt = call?.__xproRingStartedAt || null;
    const answeredAt = new Date().toISOString();
    const waitSec = ringStartedAt
      ? Math.max(0, Math.round((Date.parse(answeredAt) - Date.parse(ringStartedAt)) / 1000))
      : null;
    if (typeof call.accept === "function") {
      await call.accept();
    }
    stopIncomingCallAttention();
    publishIncomingCleared({ fromTabId: voiceTabIdRef.current });
    setIncomingCall(null);

    const parentSid = String(call?.parameters?.ParentCallSid || "").trim() || null;
    const thisSid = String(call?.parameters?.CallSid || "").trim() || null;
    const channelId =
      Number(
        call?.parameters?.ChannelId ||
          device?.__xproChannelId ||
          pendingSoftphoneRef.current?.channelId ||
          0
      ) || null;
    const attendedMode =
      resolveAttendedModeFromCall(call?.parameters) ||
      (pendingAutoAcceptRef.current ? "queue_claim" : "inbound_answer");
    const nextOutgoing = {
      toNumber: call?.parameters?.From || "inbound",
      startTime: new Date(),
      channelId,
      leadId: leadId || null,
      mode: "twilio",
      direction: "inbound",
      attendedMode,
      callInstance: call,
      callSid: parentSid || thisSid,
      parentCallSid: parentSid,
      uiInPopup: Boolean(leadId),
    };
    hangupByUserRef.current = false;
    outgoingCallRef.current = nextOutgoing;
    setOutgoingCall(nextOutgoing);
    publishActiveCallSession(serializeOutgoingCall(nextOutgoing));
    setStatus("In Call");
    setConnectingToLead(null);
    pendingAutoAcceptRef.current = null;
    attachCallDisconnectHandlers(call, nextOutgoing);

    void reportVoicePresence(channelId, {
      status: "on_call",
      callSid: parentSid || thisSid,
      leadId: leadId || null,
    });

    if (channelId && leadId && (parentSid || thisSid)) {
      void logVoiceAttempt(channelId, {
        leadId,
        callSid: parentSid || thisSid,
        status: "answered",
        answerOutcome: "answered",
        direction: "inbound",
        from: call?.parameters?.From || "",
        to: "",
        ringStartedAt,
        answeredAt,
        waitSec,
        source: "client_sdk",
        attendedMode,
        extra: voiceLogPayloadExtra(attendedMode),
      });
    }

    if (leadId) {
      const popupWin = openCallPopupWindow(leadId, { navigate });
      if (!popupWin) {
        const fallback = { ...nextOutgoing, uiInPopup: false };
        outgoingCallRef.current = fallback;
        setOutgoingCall(fallback);
        publishActiveCallSession(serializeOutgoingCall(fallback));
      }
    }
    pendingSoftphoneRef.current = null;
    return nextOutgoing;
  }, [attachCallDisconnectHandlers, device?.__xproChannelId, navigate]);

  acceptIncomingTwilioCallRef.current = acceptIncomingTwilioCall;

  const acceptCall = async () => {
    const call = incomingCall;
    if (!call) return;
    // Janela espelhada: só avisa a aba do Device. Não monta Call Twilio local.
    if (call.__xproMirrored) {
      if (typeof call.accept === "function") await call.accept();
      return;
    }
    await acceptIncomingTwilioCall(call);
  };

  const rejectCall = async () => {
    if (incomingCall) {
      const call = incomingCall;
      if (call.__xproMirrored) {
        if (typeof call.reject === "function") await call.reject();
        stopIncomingCallAttention();
        setIncomingCall(null);
        return;
      }
      const leadId =
        Number(call?.__xproLeadId || 0) ||
        resolveLeadIdFromIncoming(call, pendingSoftphoneRef.current);
      const channelId =
        Number(call?.parameters?.ChannelId || device?.__xproChannelId || 0) || null;
      const parentSid = String(call?.parameters?.ParentCallSid || "").trim();
      const thisSid = String(call?.parameters?.CallSid || "").trim();
      const callSid = parentSid || thisSid;
      const ringStartedAt = call?.__xproRingStartedAt || null;
      const rejectedAt = new Date().toISOString();
      const waitSec = ringStartedAt
        ? Math.max(0, Math.round((Date.parse(rejectedAt) - Date.parse(ringStartedAt)) / 1000))
        : null;

      clearSoftphoneAutoAccept();
      if (typeof call.reject === "function") {
        await call.reject();
      }
      stopIncomingCallAttention();
      publishIncomingCleared({ fromTabId: voiceTabIdRef.current });
      setIncomingCall(null);

      if (channelId && leadId && callSid) {
        void logVoiceAttempt(channelId, {
          leadId,
          callSid,
          status: "rejected",
          answerOutcome: "rejected",
          direction: "inbound",
          from: call?.parameters?.From || "",
          to: "",
          ringStartedAt,
          waitSec,
          source: "client_sdk",
        });
      }
    }
  };

  /** Define uma chamada recebida via socket (ex.: WhatsApp Cloud). Exibe CallNotification até aceitar/rejeitar. */
  const setIncomingCallFromSocket = ({ conversation, onAccept, onReject, kind, whatsappMeta }) => {
    setIncomingCall({
      conversation,
      kind: kind || null,
      whatsappMeta: whatsappMeta || null,
      accept: async () => {
        try {
          await onAccept?.();
        } finally {
          setIncomingCall(null);
        }
      },
      reject: async () => {
        try {
          await onReject?.();
        } finally {
          setIncomingCall(null);
        }
      },
    });
  };

  /** Fecha o CallNotification (ex.: quando a chamada é encerrada remotamente em telephony/end). */
  const clearIncomingCall = () => {
    stopIncomingCallAttention();
    publishIncomingCleared({ fromTabId: voiceTabIdRef.current });
    setIncomingCall(null);
  };

  const clearOutgoingCall = useCallback(() => {
    outgoingCallRef.current = null;
    setOutgoingCall(null);
    setStatus("Ready");
    publishCallCleared();
  }, []);

  /**
   * Fecha o softphone só se não houver Call Twilio ativa.
   * Evita sumir o teclado quando callEnded chega cedo (status ringing/answered).
   */
  const clearOutgoingCallIfIdle = useCallback(() => {
    const current = outgoingCallRef.current;
    if (!current) return;
    const call = current.callInstance;
    if (call) {
      try {
        const st =
          typeof call.status === "function"
            ? String(call.status() || "").toLowerCase()
            : "";
        // Twilio Voice SDK: connecting | ringing | open | closed
        if (st && st !== "closed") return;
        if (!st) return;
      } catch {
        return;
      }
    }
    clearOutgoingCall();
  }, [clearOutgoingCall]);

  return (
    <CallContext.Provider
      value={{
        device,
        status,
        incomingCall,
        outgoingCall,
        acceptCall,
        rejectCall,
        setIncomingCallFromSocket,
        clearIncomingCall,
        clearOutgoingCall,
        clearOutgoingCallIfIdle,
        startOutgoingCall,
        endOutgoingCall,
        sendOutgoingDtmf,
        /** Prepara o cliente WebRTC de voz no navegador para o canal (identidade alinhada à telefonia da plataforma). */
        prepareTwilioVoiceDevice,
        /** Entra numa ligação em andamento via conferência Twilio. */
        joinLiveVoiceCall,
        rememberSoftphoneIncoming,
        /** Após claim da fila: auto-aceita a próxima incoming do softphone. */
        armSoftphoneAutoAccept,
        clearSoftphoneAutoAccept,
        markBusyForQueueClaim,
        /** LeadId enquanto conecta após "Falar agora" (null = idle). */
        connectingToLead,
        softphoneError,
        clearSoftphoneError: () => setSoftphoneError(null),
        lastCallNotice,
        clearLastCallNotice: () => setLastCallNotice(null),
        mockMode,
        setMockMode,
        audioPermission,
        softphoneReady: softphoneReadyRef.current,
        setVoiceAnswerMode,
        voiceAnswerMode: answerModeRef.current,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCallContext = () => useContext(CallContext);
